/**
 * TaxonomyPicker — the controlled, presentational chip picker shared by
 * the wizard's AdditionalInfoStep, the non-wizard "add place" form, and
 * the edit form (client revision 2026-06-18, items 3 & 5).
 *
 * It renders three sections — Themes, Property Type, Communities — each as
 * a ranked Primary / Secondary / Tertiary picker (client July 2026 #7
 * unified all three kinds on the same ranked model that Themes already
 * used). Every section is a `RankedKindSection`: a stack of rank rows plus
 * an "+ Add …" button that reveals the next rank. Chips render via
 * TaxonomyChip; the catalogue comes from FilterContext (same fetch the
 * FilterBar uses, so no extra round trip).
 *
 * CONTROLLED: the component owns NO selection state. The parent passes each
 * kind's current `{1,2,3}` rank map (`themeRanks` / `propertyTypeRanks` /
 * `communityRanks`) and a per-kind pick callback. The only local state is
 * the cosmetic "how many rank rows are visible" counter per section, which
 * grows when the user clicks "+ Add …" or when the parent prefills a higher
 * rank (edit flow).
 *
 * Rank semantics live in shared helpers (setThemeAtRank / clearThemeAtRank
 * in utils/taxonomy) — this component just signals intent:
 *   - clicking the chip already at a rank  → onPick…AtRank(rank, null)
 *     (parent clears + compacts)
 *   - clicking any other chip              → onPick…AtRank(rank, id)
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFilter } from "./FilterContext";
import { TAXONOMY_KIND, isCatchAllTaxonomy } from "./utils/taxonomy";
import TaxonomyChip from "./TaxonomyChip";
import "./TaxonomyPicker.css";

const RANK_LABELS = { 1: "Primary", 2: "Secondary", 3: "Tertiary" };
const EMPTY_RANKS = { 1: null, 2: null, 3: null };

/**
 * One kind's ranked picker (Primary/Secondary/Tertiary). Presentational +
 * self-contained: owns only the cosmetic "visible rank rows" counter.
 *
 *   - `chips`         the catalogue entries for this kind.
 *   - `ranks`         the `{1,2,3}` map of picked ids (or null) for this kind.
 *   - `sectionLabel`  row-label prefix, e.g. "Themes" → "Themes — Primary".
 *   - `nounSingular`  used in the add button, e.g. "Theme" → "+ Add Secondary Theme".
 *   - `onPickAtRank`  (rank, id|null) intent callback (null = clear + compact).
 */
function RankedKindSection({
  chips,
  ranks,
  sectionLabel,
  nounSingular,
  onPickAtRank,
}) {
  const normRanks = ranks || EMPTY_RANKS;

  const claimedIds = useMemo(
    () =>
      new Set(
        [normRanks[1], normRanks[2], normRanks[3]]
          .filter((x) => x != null)
          .map((n) => Number(n)),
      ),
    [normRanks],
  );

  // How many rank rows to render. Starts at the highest occupied rank (so an
  // edit/backtrack with Secondary set shows two rows) and grows when the user
  // clicks the add button.
  const highestOccupiedRank =
    normRanks[3] != null ? 3 : normRanks[2] != null ? 2 : 1;
  const [visibleRanks, setVisibleRanks] = useState(highestOccupiedRank);

  // Grow (never shrink) when the parent prefills a higher rank after mount —
  // e.g. the edit form resolves its existing assignments asynchronously.
  useEffect(() => {
    setVisibleRanks((prev) => Math.max(prev, highestOccupiedRank));
  }, [highestOccupiedRank]);

  const handleChipClick = useCallback(
    (rank, chipId) => {
      const current = normRanks[rank] ?? null;
      // Clicking the chip already at this rank toggles it off (parent clears
      // + compacts). Any other chip sets/replaces this rank.
      onPickAtRank(rank, current === chipId ? null : chipId);
    },
    [normRanks, onPickAtRank],
  );

  const handleAddRank = useCallback(() => {
    setVisibleRanks((prev) => Math.min(prev + 1, 3));
  }, []);

  if (!chips || chips.length === 0) return null;

  // The add button only appears once the current top rank has a pick —
  // asking for a Secondary before the Primary is chosen is nonsense.
  const canAddRank = visibleRanks < 3 && normRanks[visibleRanks] != null;
  const nextRankLabel = RANK_LABELS[visibleRanks + 1];

  return (
    <div className="taxonomy-picker__section">
      {Array.from({ length: visibleRanks }, (_, i) => i + 1).map((rank) => {
        const currentId = normRanks[rank];
        return (
          <div
            key={`${sectionLabel}-rank-${rank}`}
            className="taxonomy-picker__row"
          >
            <p className="taxonomy-picker__label">
              {`${sectionLabel} — ${RANK_LABELS[rank]}`}
            </p>
            <div className="taxonomy-picker__chips">
              {chips.map((t) => {
                const tid = Number(t.id);
                const isActive = Number(currentId) === tid;
                // Disable a chip a different rank already owns (same-rank
                // stays clickable so it can toggle off).
                const claimedElsewhere = !isActive && claimedIds.has(tid);
                return (
                  <TaxonomyChip
                    key={t.id}
                    name={t.name}
                    color={t.color}
                    isActive={isActive}
                    isCatchAll={isCatchAllTaxonomy(t)}
                    disabled={claimedElsewhere}
                    onClick={() => handleChipClick(rank, tid)}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      {canAddRank && (
        <div className="taxonomy-picker__add-row">
          <button
            type="button"
            className="taxonomy-picker__add-btn"
            onClick={handleAddRank}
          >
            {`+ Add ${nextRankLabel} ${nounSingular}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default function TaxonomyPicker({
  themeRanks,
  propertyTypeRanks,
  communityRanks,
  onPickThemeAtRank,
  onPickPropertyTypeAtRank,
  onPickCommunityAtRank,
}) {
  const { taxonomies } = useFilter();

  const themes = useMemo(
    () => (taxonomies || []).filter((t) => t.kind === TAXONOMY_KIND.THEME),
    [taxonomies],
  );
  const propertyTypes = useMemo(
    () =>
      (taxonomies || []).filter((t) => t.kind === TAXONOMY_KIND.PROPERTY_TYPE),
    [taxonomies],
  );
  const communities = useMemo(
    () => (taxonomies || []).filter((t) => t.kind === TAXONOMY_KIND.COMMUNITY),
    [taxonomies],
  );
  const hasChipCatalogue =
    themes.length > 0 || propertyTypes.length > 0 || communities.length > 0;

  if (!hasChipCatalogue) return null;

  return (
    <div className="taxonomy-picker">
      <RankedKindSection
        chips={themes}
        ranks={themeRanks}
        sectionLabel="Themes"
        nounSingular="Theme"
        onPickAtRank={onPickThemeAtRank}
      />
      <RankedKindSection
        chips={propertyTypes}
        ranks={propertyTypeRanks}
        sectionLabel="Property Type"
        nounSingular="Property Type"
        onPickAtRank={onPickPropertyTypeAtRank}
      />
      <RankedKindSection
        chips={communities}
        ranks={communityRanks}
        sectionLabel="Communities"
        nounSingular="Community"
        onPickAtRank={onPickCommunityAtRank}
      />
    </div>
  );
}
